// StaffSidebar: Slide-in/out menu for staff panel
import React from 'react';
import { List, ListItem, ListItemIcon, ListItemText, ListItemButton, Tooltip, Badge } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BookIcon from '@mui/icons-material/Book';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import CategoryIcon from '@mui/icons-material/Category';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import { useLocation, Link } from 'react-router-dom';
import { useNotifications } from '../context/NotificationContext';
import { useProfile } from '../context/ProfileContext';

import StoreIcon from '@mui/icons-material/Store';
import PaymentIcon from '@mui/icons-material/Payment';
import StorageIcon from '@mui/icons-material/Storage';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

const allMenuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/staff', roles: ['staff', 'admin', 'superadmin'], permissionKey: null },
  { text: 'Bookings', icon: <BookIcon />, path: '/staff/bookings', roles: ['staff', 'admin', 'superadmin'], permissionKey: 'booking' },
  { text: 'Tickets', icon: <ConfirmationNumberIcon />, path: '/staff/tickets', roles: ['staff', 'admin', 'superadmin'], permissionKey: 'tickets' },
  { text: 'Booths', icon: <StoreIcon />, path: '/staff/booths', roles: ['staff', 'admin', 'superadmin'], permissionKey: 'booth' },
  { text: 'Payments', icon: <PaymentIcon />, path: '/staff/payments', roles: ['staff', 'admin', 'superadmin'], permissionKey: 'payment' },
  { text: 'Storage', icon: <StorageIcon />, path: '/staff/storage', roles: ['staff', 'admin', 'superadmin'], permissionKey: 'storage' },
  { text: 'Packages', icon: <LocalOfferIcon />, path: '/staff/packages', roles: ['staff', 'admin', 'superadmin'], permissionKey: 'package' },
  { text: 'CMS', icon: <CategoryIcon />, path: '/staff/cms', roles: ['staff', 'admin', 'superadmin', 'content'], permissionKey: 'cms' },
  { text: 'Notifications', icon: <NotificationsIcon />, path: '/staff/notifications', roles: ['staff', 'admin', 'superadmin'], permissionKey: null },
  { text: 'Settings', icon: <SettingsIcon />, path: '/staff/settings', roles: ['admin', 'superadmin'], permissionKey: null },
  { text: 'Speakers', icon: <PersonIcon />, path: '/staff/speakers', roles: ['staff', 'admin', 'superadmin'], permissionKey: 'speaker' },
];

function StaffSidebar({ mini }) {
  const location = useLocation();
  const { profile } = useProfile() || {};
  const user = profile || JSON.parse(localStorage.getItem('user') || '{}');
  const menuItems = allMenuItems.filter(item => {
    if (!user || !user.role) return process.env.NODE_ENV !== 'production';
    // superadmin/admin get broad access
    if (user.role === 'superadmin' || user.role === 'admin') return true;
    // If user has explicit module permissions, use them to decide visibility
    const perms = Array.isArray(user.permissions) ? user.permissions : [];
    // If item.permissionKey is set, show if user has that permission
    if (item.permissionKey && perms.includes(item.permissionKey)) return true;
    // If user has any permission matching the module (for future extensibility)
    if (item.permissionKey && perms.some(p => p === item.permissionKey)) return true;
    // treat special roles like 'booking' or 'speaker' as staff-equivalent for UI access
    const effectiveRole = ['booking', 'speaker'].includes(user.role) ? 'staff' : user.role;
    return Array.isArray(item.roles) && item.roles.includes(effectiveRole);
  });
  const { unreadCount = 0 } = useNotifications() || {};
  return (
    <List sx={{ width: mini ? 60 : 240, bgcolor: 'background.paper', height: '100%', boxShadow: 2, transition: 'width 0.3s' }}>
      {menuItems.map((item) => (
        <Tooltip title={mini ? item.text : ''} placement="right" arrow key={item.text}>
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              selected={location.pathname === item.path}
              aria-label={item.text}
              sx={{
                borderRadius: 2,
                my: 0.5,
                bgcolor: location.pathname === item.path ? 'primary.main' : 'background.paper',
                color: location.pathname === item.path ? 'primary.contrastText' : 'text.primary',
                fontWeight: location.pathname === item.path ? 'bold' : 'normal',
                boxShadow: location.pathname === item.path ? 3 : 0,
                minHeight: 48,
                justifyContent: mini ? 'center' : 'flex-start',
                px: mini ? 1 : 2,
                transition: 'background 0.2s',
              }}
            >
              <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.contrastText' : 'text.secondary', minWidth: 0, mr: mini ? 0 : 2, justifyContent: 'center' }}>
                {item.text.toLowerCase().includes('notification') ? (
                  <Badge badgeContent={unreadCount} color="error">{item.icon}</Badge>
                ) : item.icon}
              </ListItemIcon>
              {!mini && <ListItemText primary={item.text} />}
            </ListItemButton>
          </ListItem>
        </Tooltip>
      ))}
    </List>
  );
}

export default StaffSidebar;
